import { reactive, readonly } from 'vue'

export interface ConfirmOptions {
  message: string
  title?: string
  confirmText?: string
  cancelText?: string
  danger?: boolean
  /**
   * Optional async work to run after the user accepts. While it is pending the dialog
   * stays open and shows a loading state; a failure keeps the prompt open so the user
   * can retry or cancel. Without an action the dialog closes as soon as the user chooses.
   */
  action?: () => Promise<unknown>
}

interface ConfirmState {
  open: boolean
  loading: boolean
  title: string
  message: string
  confirmText: string
  cancelText: string
  danger: boolean
}

const state = reactive<ConfirmState>({
  open: false,
  loading: false,
  title: '',
  message: '',
  confirmText: '',
  cancelText: '',
  danger: false,
})

let pendingAction: (() => Promise<unknown>) | null = null
let resolveChoice: ((accepted: boolean) => void) | null = null

/** Read-only view of the singleton prompt, rendered by ConfirmDialogHost. */
export const confirmState = readonly(state)

/**
 * Opens the shared confirmation prompt and resolves with the user's choice.
 * Usable from stores and plain modules, not just components.
 */
export function confirm(options: ConfirmOptions): Promise<boolean> {
  // Replacing an already-open prompt resolves the earlier caller so nothing awaits forever.
  if (resolveChoice) {
    const previous = resolveChoice
    resolveChoice = null
    pendingAction = null
    previous(false)
  }

  state.title = options.title ?? ''
  state.message = options.message
  state.confirmText = options.confirmText ?? ''
  state.cancelText = options.cancelText ?? ''
  state.danger = options.danger ?? false
  state.loading = false
  state.open = true
  pendingAction = options.action ?? null

  return new Promise<boolean>((resolve) => {
    resolveChoice = resolve
  })
}

/** Runs the optional action, then settles the prompt. */
export async function acceptConfirm(): Promise<void> {
  if (!resolveChoice || state.loading) return

  const action = pendingAction
  if (!action) {
    settle(true)
    return
  }

  state.loading = true
  try {
    await action()
    settle(true)
  } catch {
    // Keep the prompt open so the user can retry; the action owns its error reporting.
    state.loading = false
  }
}

/** Declines the prompt. Ignored while the action is still running. */
export function dismissConfirm(): void {
  if (state.loading) return
  settle(false)
}

function settle(accepted: boolean): void {
  const resolve = resolveChoice
  resolveChoice = null
  pendingAction = null
  state.open = false
  state.loading = false
  if (resolve) resolve(accepted)
}

export function useConfirm() {
  return {
    state: confirmState,
    confirm,
    accept: acceptConfirm,
    dismiss: dismissConfirm,
  }
}
